import { formatTimezoneList } from "common";
import { isNil } from "lodash";
import React, { useCallback, useState } from "react";
import { useQuery } from "react-query";
import { Experience, Timeslot } from "types";
import { useDebounce } from "use-debounce";
import shallow from "zustand/shallow";
import { useSeller } from "../../hooks/useAccount";
import { isCheckInAllowed, isVirtualExperience } from "../../models/Experience";
import { useCreatePurchaseStore } from "../../stores/createPurchaseStore";
import { fetchCityOptions } from "../Common/CitySelect";
import { TimezonePicker } from "../Input/TimezonePicker";
import { ArrivalPicker } from "./ArrivalPicker";
import { AutoCheckIn } from "./AutoCheckIn";

interface CreatePurchaseArrivalPickerProps {
    itemIndex?: number;
}

export const CreatePurchaseArrivalPicker = React.memo(({ itemIndex }: CreatePurchaseArrivalPickerProps) => {
    const arrivalTimezoneName = useCreatePurchaseStore((state) => state.getActiveItem().arrivalTimezoneName);
    const experience = useCreatePurchaseStore((state) => state.getActiveProduct<Experience>());
    const seller = useSeller();

    const [search, setSearch] = useState("");
    const [debouncedSearch] = useDebounce(search, 500);

    const [timezone, setTimezone] = useState(arrivalTimezoneName);
    const handleTimezoneInputChange = (value: string) => setSearch(value);
    const handleTimezoneChange = (timezone?: string) => {
        setTimezone(timezone);
        useCreatePurchaseStore.getState().actions.setPurchase((draft) => {
            draft.items[itemIndex ?? 0].arrivalTimezoneName = timezone;
        });
    };

    const [arrivalDate, arrivalTime = null] = useCreatePurchaseStore(
        (state) => [state.getActiveItem().arrivalDate, state.getActiveItem().arrivalTime],
        shallow,
    );
    const activeItemIndex = useCreatePurchaseStore((state) => state.activeItemIndex);

    const index = itemIndex ?? activeItemIndex;

    const { data: options, isLoading } = useQuery(
        ["cities", debouncedSearch],
        async () => fetchCityOptions(debouncedSearch),
        { enabled: search.length > 2 },
    );

    const handleChangeArrival = useCallback(
        (date: string, time: number | null, timeslot?: Timeslot) => {
            const { setPurchase } = useCreatePurchaseStore.getState().actions;
            setPurchase((draft) => {
                if (draft.items[index]) {
                    draft.items[index].arrivalDate = date;
                    if (isNil(time)) {
                        draft.items[index].arrivalTime = undefined;
                    } else {
                        draft.items[index].arrivalTime = time;
                    }

                    draft.items[index].timeslot = timeslot ?? null;
                }

                if (timezone) {
                    if (isNil(timeslot?.time)) {
                        draft.items[index].arrivalTime = undefined;
                    } else {
                        draft.items[index].arrivalTime = timeslot!.time;
                        draft.items[index].arrivalTimezoneName = timezone;
                    }
                }
            });
        },
        [index, timezone],
    );

    return (
        <div className="flex flex-wrap justify-between">
            <div className="mr-4 flex-grow space-y-2">
                {isVirtualExperience(experience) && (
                    <TimezonePicker
                        className="w-[440px]"
                        options={formatTimezoneList(options ?? [])}
                        isLoading={isLoading}
                        value={timezone}
                        onTimezoneChange={handleTimezoneChange}
                        onInputChange={handleTimezoneInputChange}
                    />
                )}
                <ArrivalPicker
                    key={`${experience.id}_${itemIndex}`}
                    arrivalDate={arrivalDate}
                    arrivalTime={arrivalTime}
                    itemIndex={itemIndex}
                    timezone={timezone}
                    onChange={handleChangeArrival}
                />
            </div>
            <div className="shrink-0 pt-8">{isCheckInAllowed(seller, experience) && <AutoCheckIn />}</div>
        </div>
    );
});

CreatePurchaseArrivalPicker.displayName = "CreatePurchaseArrivalPicker";
